import { useEffect, useState } from 'react';
import type { PlaceSuggestion } from '@mr-website/schemas';
import { useDebounce } from '@/hooks/useDebounce';
import { usePlaceDetails, usePlaceSuggestions } from '@/hooks/usePlaces';
import { parseAddressResponse, type ResolvedAddress } from '@/lib/parseAddressResponse';

interface UseStreetAutocompleteOptions {
  input: string;
  onResolve: (resolved: ResolvedAddress) => void;
  enabled: boolean;
}

interface UseStreetAutocompleteResult {
  suggestions: PlaceSuggestion[];
  isFetching: boolean;
  handleSelect: (placeId: string) => void;
}

export function useStreetAutocomplete({
  input,
  onResolve,
  enabled,
}: UseStreetAutocompleteOptions): UseStreetAutocompleteResult {
  const [selectedPlaceId, setSelectedPlaceId] = useState<string | null>(null);

  const debouncedInput = useDebounce(input, 300);
  const { data: suggestions = [], isFetching } = usePlaceSuggestions(debouncedInput, { enabled });
  const { data: placeDetails } = usePlaceDetails(selectedPlaceId);

  useEffect(() => {
    if (!placeDetails || !selectedPlaceId) return;
    const resolved = parseAddressResponse(placeDetails);
    onResolve(resolved);
    setSelectedPlaceId(null);
  }, [placeDetails]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelect = (placeId: string) => {
    setSelectedPlaceId(placeId);
  };

  return { suggestions, isFetching, handleSelect };
}
